import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Lightbulb, Camera, Scissors, Send, ArrowRight, Instagram, Lock, Play, Calendar } from 'lucide-react';
import VideoCaseStudyModal from '../components/VideoCaseStudyModal';
import { resolveImageUrl } from '../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Twitter-style verified blue checkmark (re-used from TeamSection)
const VerifiedBadge = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-label="Verified" fill="none">
    <path fill="#1D9BF0" d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.67-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34z" />
    <path fill="#fff" d="m10.06 14.99 5.41-5.41 1.06 1.06-6.47 6.47-3.37-3.37 1.06-1.06z" />
  </svg>
);

const PROCESS_STEPS = [
  { icon: Lightbulb, title: 'Ideation', body: "We sit with you, surface the angle nobody else is telling, and lock the narrative before a single frame is shot." },
  { icon: Camera, title: 'Filming', body: "Lean crew, broadcast-grade gear, story-first directing. We shoot for the cut, not the dailies." },
  { icon: Scissors, title: 'Editing', body: "Two outputs from every shoot — short-form for distribution, long-form for legacy. Same craft on both." },
  { icon: Send, title: 'Distribution', body: "Multi-platform launch. Vertical, horizontal, newsletter, paid amplification when it makes sense." },
];

// Shared component for the homepage teaser too
export const FlytProcessStrip = ({ compact = false }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
    {PROCESS_STEPS.map((step, idx) => {
      const Icon = step.icon;
      return (
        <div
          key={step.title}
          className={`bg-white/5 backdrop-blur border border-white/10 rounded-2xl ${compact ? 'p-5' : 'p-6 lg:p-8'} text-white`}
          data-testid={`process-step-${idx}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-yellow-300" />
            </div>
            <span className="font-display text-xs text-gray-400">0{idx + 1}</span>
          </div>
          <h3 className={`font-display ${compact ? 'text-lg' : 'text-xl'} uppercase leading-tight mb-2`}>{step.title}</h3>
          {!compact && <p className="font-body text-sm text-gray-300 leading-relaxed">{step.body}</p>}
        </div>
      );
    })}
  </div>
);

// Compact client grid for homepage teaser
export const FlytClientsGrid = ({ limit = 7, showHeading = true }) => {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    axios.get(`${API}/flyt/clients`).then(r => setClients(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {showHeading && (
        <div className="text-center mb-10">
          <p className="font-italic text-yellow-300 text-sm mb-3 tracking-wider uppercase">FLYT Studios · Voices</p>
          <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight text-white">
            The people we <span className="gold-gradient">build with</span>
          </h2>
        </div>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4 lg:gap-6">
        {clients.slice(0, limit).map((c) => (
          <Link
            key={c.id}
            to={`/flyt/clients/${c.slug}`}
            className={`group text-center ${c.placeholder ? 'opacity-50 hover:opacity-80' : ''} transition`}
            data-testid={`client-tile-${c.slug}`}
          >
            <div className="relative aspect-square rounded-full overflow-hidden bg-gradient-to-br from-gray-800 via-black to-gray-900 ring-2 ring-white/10 group-hover:ring-yellow-400/60 transition shadow-xl">
              {c.thumbnail ? (
                <img src={resolveImageUrl(c.thumbnail, 400)} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <Lock className="w-6 h-6" />
                </div>
              )}
            </div>
            <p className="font-display text-xs lg:text-sm uppercase mt-3 leading-tight text-white inline-flex items-center justify-center gap-1">
              {c.name.split(' (')[0]}
              {!c.placeholder && <VerifiedBadge className="w-3.5 h-3.5" />}
            </p>
            {c.handle && <p className="text-[10px] text-gray-400 mt-0.5">@{c.handle}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
};

const FlytProcessPage = () => {
  const [examples, setExamples] = useState([]);
  const [activeExample, setActiveExample] = useState(null);

  useEffect(() => {
    axios.get(`${API}/flyt/examples`).then(r => setExamples(r.data)).catch(() => {});
  }, []);

  // Booking URL — replace this with your real Calendly / Cal.com / Savvy link any time.
  const BOOKING_URL = 'https://calendly.com/flytstudios/30min';

  return (
    <div data-testid="flyt-process-page" className="bg-black text-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle at 15% 30%, rgba(212,175,55,0.4), transparent 55%), radial-gradient(circle at 85% 70%, rgba(240,216,117,0.3), transparent 55%)'
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <p className="font-italic text-yellow-300 text-sm mb-5 tracking-wider uppercase">FLYT Studios · Process</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] mb-8">
            From idea to <span className="gold-gradient">audience</span>.<br />
            One shoot. Every platform.
          </h1>
          <p className="font-italic text-xl md:text-2xl text-gray-300 max-w-3xl leading-relaxed mb-10">
            FLYT Studios is the production arm behind Dreams League — a small crew shipping documentary-grade short and long-form content for the people audiences already trust.
          </p>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="font-italic text-yellow-300 text-sm mb-3 tracking-wider uppercase">How We Build</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
              Our <span className="gold-gradient">process</span>
            </h2>
          </div>
          <FlytProcessStrip />
        </div>
      </section>

      {/* CLIENT ROSTER */}
      <section className="py-20 lg:py-28 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FlytClientsGrid limit={13} />
          <p className="text-center text-gray-500 text-sm mt-10">Click anyone for the full case study, process &amp; behind-the-scenes.</p>
        </div>
      </section>

      {/* EXAMPLES — 2x2 vertical grid with case-study modals */}
      <section className="py-20 lg:py-28" data-testid="examples-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="font-italic text-yellow-300 text-sm mb-3 tracking-wider uppercase">Examples</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
              See the work, <span className="gold-gradient">the way</span> we built it
            </h2>
            <p className="font-body text-gray-400 text-base mt-5">
              Tap any video to play it — the ideation, goal, and approach sit right next to it.
            </p>
          </div>

          {examples.length === 0 ? (
            <p className="text-center text-gray-500 text-sm">No examples yet — add them from Admin → FLYT Examples.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:gap-5 lg:gap-7 max-w-3xl mx-auto">
              {examples.slice(0, 4).map((ex, idx) => (
                <button
                  key={ex.id}
                  type="button"
                  onClick={() => setActiveExample(ex)}
                  className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-gradient-to-br from-gray-800 to-black ring-1 ring-white/10 hover:ring-yellow-400/60 transition shadow-2xl text-left"
                  data-testid={`example-card-${idx}`}
                >
                  {ex.poster && (
                    <img loading="lazy" decoding="async"
                      src={resolveImageUrl(ex.poster, 800)}
                      alt={ex.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center group-hover:bg-yellow-300 group-hover:border-yellow-300 transition">
                      <Play className="w-7 h-7 lg:w-8 lg:h-8 text-white fill-white group-hover:text-black group-hover:fill-black ml-1 transition" />
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-4 lg:p-5">
                    {ex.client_name && (
                      <p className="font-italic text-yellow-300 text-[10px] uppercase tracking-widest mb-1">{ex.client_name}</p>
                    )}
                    <h3 className="font-display text-base lg:text-xl uppercase leading-tight">{ex.title}</h3>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Booking CTA */}
          <div className="mt-16 text-center">
            <p className="font-italic text-yellow-300 text-sm mb-3 tracking-wider uppercase">Ready to talk?</p>
            <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight mb-6 max-w-xl mx-auto">
              Book a <span className="gold-gradient">15-min call</span> and we'll map your story.
            </h3>
            <a
              href={BOOKING_URL}
              target="_blank" rel="noopener noreferrer"
              className="btn-pill bg-white text-black inline-flex items-center gap-2 font-bold"
              data-testid="booking-cta-btn"
            >
              <Calendar className="w-4 h-4" /> Book a Call
            </a>
            <p className="text-xs text-gray-500 mt-3">No pitch deck. Just a 15-minute conversation.</p>
          </div>
        </div>
      </section>

      {/* Modal */}
      <VideoCaseStudyModal example={activeExample} onClose={() => setActiveExample(null)} />

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] mb-6">
            Got a story we should <span className="gold-gradient">tell?</span>
          </h2>
          <Link to="/flyt/contact" className="btn-pill bg-white text-black inline-flex items-center gap-2 font-bold">
            Pitch a Story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FlytProcessPage;
